package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.runtime.*
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLVideoElement

@OptIn(ExperimentalComposeUiApi::class)
@Composable
actual fun QrCameraScanner(
    isScanningEnabled: Boolean,
    onQrCodeScanned: (String) -> Unit,
    modifier: Modifier,
) {
    val latestOnQrCodeScanned by rememberUpdatedState(onQrCodeScanned)
    var scannerController by remember { mutableStateOf<BrowserQrScanner?>(null) }

    WebElementView(
        factory = {
            val scannerElement = createScannerElement { qrCode ->
                latestOnQrCodeScanned(qrCode)
            }
            scannerController = scannerElement.scanner
            scannerElement.root
        },
        modifier = modifier,
        update = {
            scannerController?.setScanningEnabled(isScanningEnabled)
        },
        onRelease = {
            scannerController?.stop()
            scannerController = null
        },
    )
}

private data class ScannerElement(
    val root: HTMLElement,
    val scanner: BrowserQrScanner,
)

private fun createScannerElement(onQrCodeScanned: (String) -> Unit): ScannerElement {
    val root = document.createElement("div") as HTMLElement
    root.setAttribute(
        "style",
        """
            width: 100%;
            height: 100%;
            min-height: 100%;
            background: transparent;
            color: #fff;
            position: relative;
            overflow: hidden;
            pointer-events: none;
        """.trimIndent(),
    )

    val fallback = document.createElement("div") as HTMLElement
    fallback.textContent = "Starting camera..."
    fallback.setAttribute(
        "style",
        """
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px;
            box-sizing: border-box;
            background: #000;
            color: #fff;
            text-align: center;
            font: 600 16px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        """.trimIndent(),
    )
    root.appendChild(fallback)

    val container = document.createElement("div") as HTMLElement
    container.setAttribute(
        "style",
        """
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            min-height: 100%;
            overflow: hidden;
            background: #000;
            pointer-events: none;
        """.trimIndent(),
    )
    root.appendChild(container)

    val video = document.createElement("video") as HTMLVideoElement
    video.setAttribute(
        "style",
        """
            width: 100%;
            height: 100%;
            min-height: 100%;
            object-fit: cover;
            display: block;
        """.trimIndent(),
    )
    video.setAttribute("playsinline", "true")
    video.autoplay = true
    video.muted = true
    container.appendChild(video)

    container.appendChild(createScannerChrome())

    val message = document.createElement("div") as HTMLElement
    message.setAttribute(
        "style",
        """
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px;
            color: white;
            background: rgba(0, 0, 0, 0.78);
            text-align: center;
            font: 600 16px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            line-height: 1.35;
            box-sizing: border-box;
            z-index: 4;
        """.trimIndent(),
    )
    message.textContent = "Starting camera..."
    container.appendChild(message)

    val scanner = BrowserQrScanner(container, fallback, message, video, onQrCodeScanned)
    scanner.start()

    return ScannerElement(root, scanner)
}

private class BrowserQrScanner(
    private val container: HTMLElement,
    private val fallback: HTMLElement,
    private val message: HTMLElement,
    private val video: HTMLVideoElement,
    private val onQrCodeScanned: (String) -> Unit,
) {
    private var stream: dynamic = null
    private var detector: dynamic = null
    private var intervalId: Int? = null
    private var isScanningEnabled = true
    private var isDetecting = false
    private var currentMessage: String? = "Starting camera..."

    fun start() {
        if (!hasCameraSupport()) {
            showMessage("Camera access is not available in this browser. Use HTTPS or localhost.")
            return
        }

        if (!hasBarcodeDetectorSupport()) {
            showMessage("QR scanning is not supported in this browser. Please use Chrome or Edge.")
            return
        }

        try {
            detector = js("new BarcodeDetector({ formats: ['qr_code'] })")

            val constraints = js(
                """
                    ({
                        video: {
                            facingMode: { ideal: 'environment' }
                        },
                        audio: false
                    })
                """
            )

            getUserMedia(constraints)
                .then { mediaStream: dynamic ->
                    stream = mediaStream
                    video.asDynamic().srcObject = mediaStream
                    video.play()
                    showMessage(null)
                    intervalId = window.setInterval(
                        handler = { detectFrame() },
                        timeout = 500,
                    )
                }
                .catch { _: dynamic ->
                    showMessage("Camera access is required to scan attendee QR codes.")
                }
        } catch (error: Throwable) {
            showMessage("Could not start the camera scanner in this browser.")
        }
    }

    fun setScanningEnabled(enabled: Boolean) {
        isScanningEnabled = enabled
        container.style.display = if (enabled) "block" else "none"
        applyMessageVisibility()
    }

    fun stop() {
        intervalId?.let(window::clearInterval)
        intervalId = null
        video.pause()
        video.asDynamic().srcObject = null

        stream?.getTracks()?.forEach { track: dynamic ->
            track.stop()
        }
        stream = null
    }

    private fun showMessage(text: String?) {
        currentMessage = text
        fallback.textContent = text ?: "Starting camera..."
        message.textContent = text.orEmpty()
        applyMessageVisibility()
    }

    private fun applyMessageVisibility() {
        val display = if (isScanningEnabled && currentMessage != null) "flex" else "none"
        fallback.style.display = display
        message.style.display = display
    }

    private fun detectFrame() {
        if (!isScanningEnabled || isDetecting || video.readyState < 2) return

        isDetecting = true
        detector.detect(video)
            .then { barcodes: dynamic ->
                val first = barcodes[0]
                val rawValue = first?.rawValue as? String
                if (!rawValue.isNullOrBlank()) {
                    onQrCodeScanned(rawValue)
                }
            }
            .finally {
                isDetecting = false
            }
    }
}

private fun isWebQrScannerSupported(): Boolean {
    return hasCameraSupport() && hasBarcodeDetectorSupport()
}

private fun hasCameraSupport(): Boolean {
    return js(
        """
            !!(
                typeof navigator !== 'undefined' &&
                navigator.mediaDevices &&
                navigator.mediaDevices.getUserMedia
            )
        """
    ) as Boolean
}

private fun hasBarcodeDetectorSupport(): Boolean {
    return js(
        """
            !!(typeof BarcodeDetector !== 'undefined')
        """
    ) as Boolean
}

private fun getUserMedia(constraints: dynamic): dynamic {
    return window.navigator.asDynamic().mediaDevices.getUserMedia(constraints)
}

private fun createScannerChrome(): HTMLElement {
    val overlay = document.createElement("div") as HTMLElement
    overlay.setAttribute(
        "style",
        """
            position: absolute;
            inset: 0;
            pointer-events: none;
            color: white;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            z-index: 3;
        """.trimIndent(),
    )

    val scanBox = document.createElement("div") as HTMLElement
    scanBox.setAttribute(
        "style",
        """
            position: absolute;
            width: min(72vw, 72vh);
            height: min(72vw, 72vh);
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            border: 2px solid rgba(255, 255, 255, 0.95);
            border-radius: 24px;
            box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.52);
            box-sizing: border-box;
        """.trimIndent(),
    )
    overlay.appendChild(scanBox)

    val line = document.createElement("div") as HTMLElement
    line.setAttribute(
        "style",
        """
            position: absolute;
            left: 18px;
            right: 18px;
            top: 18px;
            height: 2px;
            border-radius: 999px;
            background: rgba(137, 248, 199, 0.95);
            animation: rainbowScannerLine 1.5s linear infinite alternate;
        """.trimIndent(),
    )
    scanBox.appendChild(line)

    val hint = document.createElement("div") as HTMLElement
    hint.textContent = "Scan attendee QR code"
    hint.setAttribute(
        "style",
        """
            position: absolute;
            left: 0;
            right: 0;
            top: calc(50% + min(36vw, 36vh) + 28px);
            text-align: center;
            font-size: 16px;
            font-weight: 500;
            text-shadow: 0 1px 8px rgba(0, 0, 0, 0.55);
        """.trimIndent(),
    )
    overlay.appendChild(hint)

    ensureScannerAnimationStyle()
    return overlay
}

private fun ensureScannerAnimationStyle() {
    if (document.getElementById("rainbow-scanner-style") != null) return

    val style = document.createElement("style") as HTMLElement
    style.id = "rainbow-scanner-style"
    style.textContent = """
        @keyframes rainbowScannerLine {
            from { top: 18px; }
            to { top: calc(100% - 20px); }
        }
    """.trimIndent()
    document.head?.appendChild(style)
}
